import React, { useState, useEffect, useRef, startTransition } from "react";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import TournamentExportModal from "./export/TournamentExportModal";
import TournamentShareModal from "./export/TournamentShareModal";
import useAxios from "../../utils/useAxios";
import { useLocation, useHistory, Link } from "react-router-dom";
import { copyTournamentFull } from "../../utils/copyTournament";
import { copyProgressLabels, copyProgressSteps } from "../../components/TournamentMenuList";

const TournamentShareMenu = ({
                                 id, user, tournament, isAuthorized, setOpenLanguage, setOpenTheme,
                                 theme, setTheme, accent, setAccent, compactTable, setCompactTable,
                                 language, handleLanguageChange, onOpenSettings
                             }) => {
    const [openMenu, setOpenMenu] = useState(false);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const { t } = useTranslation();
    const menuRef = useRef();
    const api = useAxios();

    const location = useLocation();
    const history = useHistory();

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpenMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleCopyTournament = async (e) => {
        e.preventDefault();
        if (isCopying) return;
        setOpenMenu(false);
        setIsCopying(true);
        // Live progress in a single updating toast, same pattern as
        // TournamentMenuList.js's own copy flow - each phase updates the
        // same toast instead of leaving a blank spinner up for the whole
        // several-request copy sequence. `progress` is a real fraction of
        // copyProgressSteps done, not a fake/animated bar.
        const toastId = toast.success(t(copyProgressLabels.info), { autoClose: false, progress: 0 });
        try {
            const newId = await copyTournamentFull(api, id, (step) => {
                const stepIndex = copyProgressSteps.indexOf(step);
                toast.update(toastId, {
                    render: t(copyProgressLabels[step]),
                    progress: (stepIndex + 1) / copyProgressSteps.length,
                });
            });
            // No `progress` here - releases controlled mode so autoClose's
            // own countdown bar animates normally instead of sitting stuck
            // at the last controlled value.
            toast.update(toastId, { render: t("Tournament copied"), type: toast.TYPE.SUCCESS, autoClose: 2000 });
            window.location.href = `/tournaments/${newId}/?admin=1`;
        } catch (error) {
            console.error("Copy error:", error.response?.data || error);
            toast.update(toastId, { render: t("Failed to copy tournament"), type: toast.TYPE.ERROR, autoClose: 4000 });
        } finally {
            setIsCopying(false);
        }
    };

    const handleDeleteTournament = async (e) => {
        e.preventDefault();
        if (!window.confirm(t("Delete tournament?"))) return;
        setOpenMenu(false);
        try {
            await api.delete(`/api/v1.0/tournaments/${id}/`);
            toast.success(t("Tournament deleted"));
            history.push(isAuthorized ? "/admin/tournaments" : "/tournaments");
        } catch (error) {
            console.error(error);
            if (error.response?.status === 403) {
                toast.error(t("You do not have permission to delete this tournament"));
            }
        }
    };

    const handleGenerateGuestLink = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post(`/api/v1.0/tournaments/${id}/guest-token/`);
            const token = res.data.token || res.data.key;

            const url = `${window.location.origin}/tournaments/${id}?guest_token=${token}`;

            await navigator.clipboard.writeText(url);
            toast.success(t("Guest link copied to clipboard"));
        } catch (error) {
            toast.error(t("Error generating link"));
        }
    };

    return (
        <div ref={menuRef} style={{ position: "relative" }}>
            <div
                className={`menu-trigger ${openMenu ? 'active' : ''}`}
                onClick={(e) => {
                    e.preventDefault();
                    setOpenMenu(!openMenu);
                    setIsShareOpen(false);
                    setIsExportOpen(false);
                    setOpenLanguage(false);
                    setOpenTheme(false);
                }}
            >
                <center>
                    <FeatherIcon icon="more-vertical" style={{ width: "18px", height: "18px" }} />
                </center>
            </div>

            <div className="dots_menu_container">
                <div className="relative">
                    <div className={`options-menu ${openMenu ? 'block_active' : 'block_inactive'}`}>
                        {/* Добавлен maxHeight для скролла, если контент слишком длинный на мелких экранах */}
                        <div className="options-content-block" style={{ overflowY: "auto", paddingRight: "5px", maxHeight: "450px" }}>
                            {user && tournament?.can_edit && (
                                <>
                                    <div style={{ padding: '5px 10px', fontSize: '11px', textTransform: 'uppercase', color: 'var(--grey-blue)' }}>
                                        {t('Select view mode')}
                                    </div>

                                    <a
                                        href="#"
                                        className={`option pointer ${!isAuthorized ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setOpenMenu(false);
                                            const params = new URLSearchParams(location.search);
                                            params.delete("admin");
                                            // keeps a lazy-loaded, not-yet-shown is_authorized
                                            // branch (e.g. TournamentInformationForm's editable
                                            // fields) from tripping React 18's "suspended during
                                            // synchronous input" warning/fallback-flash.
                                            startTransition(() => {
                                                history.replace({ pathname: `/tournaments/${id}`, search: params.toString() });
                                            });
                                        }}
                                        style={{ border: 0, display: "block" }}
                                    >
                                        <FeatherIcon icon="eye" /> {t('Public View')}
                                    </a>
                                    <a
                                        href="#"
                                        className={`option pointer ${isAuthorized ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setOpenMenu(false);
                                            const params = new URLSearchParams(location.search);
                                            params.set("admin", "1");
                                            startTransition(() => {
                                                history.replace({ pathname: `/tournaments/${id}`, search: params.toString() });
                                            });
                                        }}
                                        style={{ border: 0, display: "block" }}
                                    >
                                        <FeatherIcon icon="edit" /> {t('Admin View')}
                                    </a>
                                    {isAuthorized && (
                                        <>
                                            <div style={{ borderTop: '1px solid var(--white-gray)', margin: '5px 0' }}></div>
                                            <a
                                                href="#"
                                                className="option pointer"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setOpenMenu(false);
                                                    onOpenSettings?.();
                                                }}
                                                style={{ border: 0, display: "block" }}
                                            >
                                                <FeatherIcon icon="settings" /> {t('Settings')}
                                            </a>
                                        </>
                                    )}
                                    <div style={{ borderTop: '1px solid var(--white-gray)', margin: '5px 0' }}></div>
                                </>
                            )}

                            {/*<div style={{ padding: '5px 10px', fontSize: '11px', textTransform: 'uppercase', color: 'var(--grey-blue)' }}>*/}
                            {/*    {t('Export & Share')}*/}
                            {/*</div>*/}

                            <a
                                href="#"
                                className="option pointer"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setOpenMenu(false);
                                    setIsExportOpen(false);
                                    setIsShareOpen(true);
                                }}
                                style={{ border: 0, display: "block" }}
                            >
                                <FeatherIcon icon="share-2" /> {t("Share")}
                            </a>

                            <a
                                href="#"
                                className="option pointer"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setOpenMenu(false);
                                    setIsShareOpen(false);
                                    setIsExportOpen(true);
                                }}
                                style={{ border: 0, display: "block" }}
                            >
                                <FeatherIcon icon="download" /> {t("Export")}
                            </a>

                            {user && tournament?.can_edit && isAuthorized && (
                                <a
                                    href="#"
                                    className={`option pointer ${isCopying ? "disabled" : ""}`}
                                    onClick={isCopying ? (e) => e.preventDefault() : handleCopyTournament}
                                    aria-disabled={isCopying}
                                    style={{ border: 0, display: "block" }}
                                >
                                    <FeatherIcon icon="copy" /> {isCopying ? t("Copying...") : t("Copy")}
                                </a>
                            )}

                            {user && tournament?.can_edit && isAuthorized && (
                                <a
                                    href="#"
                                    className="option pointer"
                                    onClick={handleDeleteTournament}
                                    style={{ border: 0, display: "block", color: "var(--bright-alarm)" }}
                                >
                                    <FeatherIcon icon="trash-2" /> {t("Delete")}
                                </a>
                            )}

                            <div style={{ borderTop: '1px solid var(--white-gray)', margin: '5px 0' }}></div>
                            <div style={{ padding: '5px 10px', fontSize: '11px', textTransform: 'uppercase', color: 'var(--grey-blue)' }}>
                                {t('Theme selection')}
                            </div>

                            <a
                                href="#"
                                className={`option pointer ${theme === 'light' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setTheme('light');
                                }}
                                style={{ border: 0, display: "block" }}
                            >
                                <FeatherIcon icon="sun" /> {t('Light Mode')}
                            </a>

                            <a
                                href="#"
                                className={`option pointer ${theme === 'dark' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setTheme('dark');
                                }}
                                style={{ border: 0, display: "block" }}
                            >
                                <FeatherIcon icon="moon" /> {t('Dark Mode')}
                            </a>
                            <div style={{ display: 'flex', gap: '10px', padding: '5px 10px 10px 15px' }}>
                                <div
                                    title="Grass"
                                    onClick={() => setAccent('grass')}
                                    style={{ width: 20, height: 20, borderRadius: '50%', background: '#539447', cursor: 'pointer', border: accent === 'grass' ? '2px solid var(--darker-green)' : '2px solid var(--body-background)'}}
                                />
                                <div
                                    title="Clay"
                                    onClick={() => setAccent('clay')}
                                    style={{ width: 20, height: 20, borderRadius: '50%', background: '#c6806a', cursor: 'pointer', border: accent === 'clay' ? '2px solid var(--darker-green)' : '2px solid var(--body-background)'}}
                                />
                                <div
                                    title="Hard"
                                    onClick={() => setAccent('hard')}
                                    style={{ width: 20, height: 20, borderRadius: '50%', background: '#5290cd', cursor: 'pointer', border: accent === 'hard' ? '2px solid var(--darker-green)' : '2px solid var(--body-background)'}}
                                />
                            </div>

                            {/* Independent of Light/Dark above - a layout density toggle, not a
                                third theme variant. Mirrors Header.js's own compact-table-toggle
                                li (hidden on tournament pages, see Header.js's isTournamentPage
                                guard) - this menu is that page's only reachable copy of it. See
                                frontend-tasks/2026-09-02-compact-table-mode.md.
                                Styled as the same option/pointer pair as Public/Admin View and
                                Light/Dark Mode above, instead of a standalone switch.
                                Compact Table temporarily disabled - see ThemeContext.js.
                            <a
                                href="#"
                                className={`option pointer ${!compactTable ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setCompactTable(false);
                                }}
                                style={{ border: 0, display: "block" }}
                            >
                                <FeatherIcon icon="maximize-2" /> {t('Standard mode')}
                            </a>

                            <a
                                href="#"
                                className={`option pointer ${compactTable ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setCompactTable(true);
                                }}
                                style={{ border: 0, display: "block" }}
                            >
                                <FeatherIcon icon="minimize-2" /> {t('Compact')}
                            </a> */}

                            <div style={{ borderTop: '1px solid var(--white-gray)', margin: '5px 0' }}></div>
                            <div style={{ padding: '5px 10px', fontSize: '11px', textTransform: 'uppercase', color: 'var(--grey-blue)' }}>
                                {t('Language selection')}
                            </div>
                            <a
                                href="#"
                                className={`option pointer ${language === 'uk' ? 'active' : ''}`}
                                onClick={(e) => { e.preventDefault(); handleLanguageChange('uk')(); }}
                                style={{ border: 0, display: "block", position: "relative" }}
                            >
                                <img src={require(`../../fonts/flags/1x1/ua.svg`).default} alt="UA" style={{ position: "absolute", top: "10px", left: "10px", width: "16px" }} /> Українська
                            </a>
                            <a
                                href="#"
                                className={`option pointer ${language === 'en' ? 'active' : ''}`}
                                onClick={(e) => { e.preventDefault(); handleLanguageChange('en')(); }}
                                style={{ border: 0, display: "block", position: "relative" }}
                            >
                                <img src={require(`../../fonts/flags/1x1/us.svg`).default} alt="US" style={{ position: "absolute", top: "10px", left: "10px", width: "16px" }} /> English
                            </a>

                        </div>
                    </div>
                </div>
            </div>

            <TournamentShareModal
                isOpen={isShareOpen}
                onClose={() => setIsShareOpen(false)}
                tournamentId={id}
                tournament={tournament}
            />
            <TournamentExportModal
                isOpen={isExportOpen}
                onClose={() => setIsExportOpen(false)}
                tournamentId={id}
                tournament={tournament}
            />
        </div>
    );
};

export default TournamentShareMenu;